import { differenceBy, get, set } from "lodash";
import PhotoSwipe from "photoswipe";
import PhotoSwipeUI_Default from "photoswipe/src/js/ui/photoswipe-ui-default";
import { MediaGet, thor } from "../../typings/thor";
import { Events } from "../../thor.enums";
import sanitizeAndExtractHtml from "../../utils/sanitize-and-extract-html";

interface Slide {
    src?: string;
    html?: string;
    w?: number;
    h?: number;
    title?: string;
    originalMedia: MediaGet;
}

export class MediaGalleryController {
    public static $inject: string[] = [
        "$mdGesture",
        "$mdMedia",
        "$rootScope",
        "$scope",
        "$timeout",
        "$translate",
        "itemService",
        "mediaEditorService",
        "mediaGalleryService",
        "rewriteUrlImg",
        "toaster",
        "utilsService"
    ];
    public context: string;

    private gallery;

    private options: any = {
        closeOnScroll: false,
        closeOnVerticalDrag: false,
        clickToCloseNonZoomable: false,
        closeElClasses: false,
        escKey: true,
        history: false,
        index: 0
    };
    private isSidenavOpen: boolean = false;
    private resource: thor.ItemGet;
    private slides: Slide[] = [];

    private unregisterCurrentItemListener: Function;
    private unregisterMediasListener: Function;
    private unregisterSidenavOpenListener: Function;
    private $t: Function;

    constructor(
        private $mdGesture,
        private $mdMedia: ng.material.IMedia,
        private $rootScope: ng.IRootScopeService,
        private $scope: ng.IScope,
        private $timeout: ng.ITimeoutService,
        private $translate: ng.translate.ITranslateService,
        private itemService,
        public mediaEditorService,
        public mediaGalleryService,
        private rewriteUrlImg,
        private toaster,
        private utils
    ) {
        this.$t = this.$translate.instant;
        // `skipClickHijack === true` fallback
        this.addTouchEvents();

        $scope.$watch(
            () => {
                return mediaGalleryService.isOpen;
            },
            isOpen => {
                if (isOpen) {
                    this.context = mediaGalleryService.context;
                    this.resource = mediaGalleryService.resource;

                    if (!get(this.resource, "medias")) {
                        const organizationId = get(this.resource, "organization.id", null);
                        this.itemService
                            .getMedias(this.resource.id, organizationId)
                            .then(medias => {
                                this.resource.medias = medias;
                                if (this.resource.hasOwnProperty("media_count")) {
                                    (this.resource as thor.ItemGet).media_count = medias.length;
                                }
                                this.startGallery();
                                this.startListeners();
                            })
                            .catch(error => {
                                this.toaster.show(
                                    this.$t("une_erreur_est_survenue_pendant_le_chargement_des_medias"),
                                    true
                                );
                                this.close();
                            });
                    } else {
                        (this.resource as thor.ItemGet).media_count = (this.resource as thor.ItemGet).medias.length;
                        this.startGallery();
                        this.startListeners();
                    }
                }
            }
        );
    }

    public close() {
        this.removeListeners();

        if (this.gallery) {
            this.gallery.close();
        }

        this.gallery = null;
        this.mediaGalleryService.close();
        this.isSidenavOpen = false;
    }

    public openMediaEditor() {
        const originalMedia = this.gallery.currItem.originalMedia;

        this.mediaEditorService.open(
            originalMedia,
            this.$scope,
            event => {
                this.gallery.currItem.src = this.rewriteUrlImg.buildUrl(originalMedia.url, {
                    transform: event.transform
                });
                this.gallery.currItem.w = 0;
                this.gallery.currItem.h = 0;
                set(originalMedia, "meta.transform", event.transform);

                this.gallery.invalidateCurrItems();
                this.resizeImages();

                // no check to translations status needed
                if (this.$rootScope.isRouteOnline) {
                    this.$rootScope.$broadcast(Events.OnlineItemUpdated);
                }
                this.$rootScope.$broadcast(Events.ItemUpdated, this.resource);
                this.$rootScope.$broadcast(Events.PostMessageToV2);
            },
            this.resource.id
        );
    }

    public toggleSidenav() {
        this.isSidenavOpen = !this.isSidenavOpen;
    }

    private startListeners() {
        this.unregisterCurrentItemListener = this.$scope.$watch(
            () => this.gallery.currItem,
            (_, previous) => {
                if (this.mediaGalleryService.isOpen && previous.html) {
                    const pswpContainer: any = document.querySelectorAll(".pswp__container")[0];
                    this.stopVideo(pswpContainer);
                }
            }
        );

        this.unregisterSidenavOpenListener = this.$scope.$watch(
            () => this.isSidenavOpen,
            (current, previous) => {
                if (this.mediaGalleryService.isOpen && current !== previous) {
                    this.resizeImages();
                }
            },
            true
        );

        this.unregisterMediasListener = this.$scope.$watchCollection(
            () => this.resource.medias,
            (current, previous) => {
                if (this.mediaGalleryService.isOpen !== true) {
                    return;
                }

                // media update
                if (previous && current && current.length === previous.length) {
                    current.forEach(findCurrent => {
                        const findPrevious = previous.find(
                            o => o.id ? o.id === findCurrent.id : o.url === findCurrent.url
                        );
                        if (!angular.equals(findPrevious, findCurrent)) {
                            const findIndexMedia = this.gallery.items.findIndex(
                                o => o.originalMedia.id === findCurrent.id
                            );
                            this.gallery.items[findIndexMedia] = this.mediaToSlide(findCurrent, false);
                            if (this.gallery.items[findIndexMedia].originalMedia.meta.video_embed) {
                                this.gallery.items.splice(findIndexMedia + 1, 0, this.mediaToSlide(findCurrent, true));
                            }
                            this.gallery.invalidateCurrItems();
                            this.gallery.updateSize(true);
                        }
                    });
                }

                // media add or delete
                if (previous && current && current.length !== previous.length) {
                    if (current.length > previous.length) {
                        // added media
                        const addedMedias = differenceBy(current, previous, "url");

                        addedMedias.forEach((addedMedia: any) => {
                            this.gallery.items.push(this.mediaToSlide(addedMedia, false));
                        });
                    } else if (current.length < previous.length) {
                        // deleted media
                        const deletedMedia: any = differenceBy(previous, current, "id");
                        const file = deletedMedia[0].url.split("/").pop().split("?")[0];
                        this.gallery.items.forEach((item, index) => {
                            if (item.html?.includes(file) || item.src?.includes(file)) {
                                // We delete 2 items when video is set
                                this.gallery.items.splice(index, item.originalMedia?.meta?.video_embed ? 2 : 1);
                            }
                        });
                    }

                    this.gallery.invalidateCurrItems();
                    this.gallery.updateSize(true);
                    this.gallery.ui.update();
                    this.updateImageCounter();

                    if (current.length === 0) {
                        this.close();
                    }
                }
            }
        );
    }

    private removeListeners() {
        this.unregisterCurrentItemListener();
        this.unregisterSidenavOpenListener();
        this.unregisterMediasListener();
    }

    private addTouchEvents() {
        if (this.$mdGesture.isHijackingClicks === false) {
            // close
            const closeBtn = document.querySelectorAll(".thor-pswp__close")[0];
            closeBtn.addEventListener("touchstart", e => {
                e.preventDefault();
                this.safeApply(() => {
                    this.close();
                });
            });

            // toggle pswp sidenav
            const navBtnList: NodeListOf<Element> = document.querySelectorAll(".thor-pswp__toggle__nav");
            const navBtnArr: any[] = Array.prototype.slice.call(navBtnList, 0);
            navBtnArr.forEach(btn => {
                btn.addEventListener("touchstart", e => {
                    e.preventDefault();
                    this.safeApply(() => {
                        this.isSidenavOpen = !this.isSidenavOpen;
                    });
                });
            });

            // previous slide
            const prevBtn = document.querySelectorAll(".thor-pswp__arrows.prev")[0];
            prevBtn.addEventListener("touchstart", e => {
                e.preventDefault();
                this.safeApply(() => {
                    this.gallery.prev();
                });
            });

            // next slide
            const nextBtn = document.querySelectorAll(".thor-pswp__arrows.next")[0];
            nextBtn.addEventListener("touchstart", e => {
                e.preventDefault();
                this.safeApply(() => {
                    this.gallery.next();
                });
            });
        }
    }

    private updateDownloadImageLink() {
        const currentMedia = this.gallery.currItem || {};

        if (currentMedia.src) {
            const hiResImage = currentMedia.src.split("?");
            document
                .querySelectorAll(".thor-pswp__download__link")[0]
                .setAttribute("href", hiResImage[0]);
        }
    }

    private updateImageCounter() {
        const indexIndicatorDOMElement = document.querySelectorAll(".pswp__counter")[0].innerHTML;
        document.querySelectorAll(".thor-pswp__caption__counter")[0].innerHTML = indexIndicatorDOMElement;
    }

    private initSlides() {
        this.slides = [];
        this.resource.medias.forEach(media => {
            this.slides.push(this.mediaToSlide(media, false));
            if (media.meta?.video_embed) {
                this.slides.push(this.mediaToSlide(media, true));
            }
        });
    }

    private mediaToSlide(media: MediaGet, displayHtml: boolean): Slide {
        const { name: title, transform = null, video_embed } = media.meta || {};
        const slide = { title, w: 0, h: 0, originalMedia: media };
        const src = this.rewriteUrlImg.buildUrl(media.url, { transform });

        if (displayHtml) {
            const html = this.utils.videoEmbedObjToString(sanitizeAndExtractHtml(video_embed)[0]);
            return { ...slide, html };
        }
        return { ...slide, src };
    }

    private resizeImages() {
        this.$timeout(() => this.gallery.updateSize(true), 200);
    }

    private safeApply(fn) {
        const phase = this.$scope.$root.$$phase;

        if (phase === "$apply" || phase === "$digest") {
            fn();
        } else {
            this.$scope.$apply(fn);
        }
    }

    private startGallery() {
        const pswpElement: any = document.querySelectorAll(".pswp")[0];
        this.isSidenavOpen = this.$mdMedia("gt-xs");
        this.resizeImages();
        this.initSlides();

        this.gallery = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, this.slides, this.options);

        this.gallery.listen("afterChange", () => {
            this.safeApply(() => {
                this.updateDownloadImageLink();
                this.updateImageCounter();
            });
        });

        // workaround : https://github.com/dimsemenov/PhotoSwipe/issues/796
        this.gallery.listen("gettingData", (index, item) => {
            this.safeApply(() => {
                if (item.w < 1 || item.h < 1) {
                    const img = new Image();

                    img.onload = () => {
                        if (this.mediaGalleryService.isOpen) {
                            item.w = img.width;
                            item.h = img.height;
                            this.gallery.invalidateCurrItems();
                            this.gallery.updateSize(true);
                        }
                    };

                    img.src = item.src;
                }
            });
        });

        this.gallery.listen("close", () => {
            this.safeApply(() => {
                this.close();
            });
        });

        this.gallery.init();
    }

    private stopVideo(element) {
        const iframes = element.querySelectorAll("iframe");

        if (iframes) {
            for (const iframe of iframes) {
                const iframeSrc = iframe.src;
                iframe.src = iframeSrc;
            }
        }
    }
}
